import * as React from 'react';

/** Material UI Packages. */
import { DataGridPremium, GridToolbar, GridToolbarQuickFilter, useGridApiRef } from '@mui/x-data-grid-premium';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { gridClasses } from '@mui/x-data-grid';
import { Box } from '@mui/material';
import { alpha, styled } from '@mui/material/styles';

export default function SingleDatagrid({
    rows,
    cols,
    columnGroupingModel,
    maxHeight,
    sx,
    processRowUpdate,
    getDetailPanelHeight,
    getDetailPanelContent,
    detailPanelExpandedRowIds,
    onDetailPanelExpandedRowIdsChange,
    initialState,
    onCellKeyDown,
    onProcessRowUpdateError,
    additional,
    autoHeight,
    cellSelection,
}) {
    const apiRef = useGridApiRef();

    const handleKeyDown = (params, event) => {
        if (onCellKeyDown) {
            onCellKeyDown(apiRef)(params, event);
        } else {
            handleTabKeyDown(apiRef)(params, event);
        }
    };

    return (
        <DataGridPremium
            rows={rows}
            columns={cols}
            density="compact"
            disableColumnMenu
            disableColumnSorting
            hideFooter
            initialState={{
                columns: {
                    columnVisibilityModel: {
                        id: false,
                    },
                },
                ...initialState,
            }}
            processRowUpdate={processRowUpdate}
            columnGroupingModel={columnGroupingModel}
            columnGroupHeaderHeight={36}
            rowHeight={45}
            columnHeaderHeight={45}
            getDetailPanelHeight={getDetailPanelHeight}
            getDetailPanelContent={getDetailPanelContent}
            detailPanelExpandedRowIds={detailPanelExpandedRowIds}
            onDetailPanelExpandedRowIdsChange={onDetailPanelExpandedRowIdsChange}
            sx={{
                width: '100%',
                maxHeight: { maxHeight },
                border: 'none',
                fontSize: '11px',
                borderRadius: 0,
                '& .MuiDataGrid-columnSeparator': {
                    display: 'none',
                },
                '& .MuiDataGrid-columnHeader': {
                    backgroundColor: '#f1f1f1',
                    textAlign: 'center',
                },
                '& .MuiDataGrid-cell': {
                    p: 0,
                },
                '& .MuiInputBase-input': {
                    px: 1,
                    fontSize: '11px',
                },
                '& .MuiDataGrid-row.Mui-selected': {
                    backgroundColor: 'white !important',
                    '&:hover': {
                        backgroundColor: 'white !important',
                    },
                },
                '& .MuiDataGrid-scrollbarFiller': {
                    backgroundColor: '#f1f1f1',
                },

                ...sx,
            }}
            apiRef={apiRef}
            onCellKeyDown={handleKeyDown}
            onProcessRowUpdateError={onProcessRowUpdateError}
            autoHeight={autoHeight}
            cellSelection={cellSelection}
            rowSelection={cellSelection ? false : undefined}
            {...additional}
        />
    );
}
function CustomToolbar() {
    return (
        <Box sx={{ display: 'flex', justifyContent: 'right', mb: '5px' }}>
            <GridToolbarQuickFilter />
        </Box>
    );
}

export function MainDatagrid({
    rows,
    cols,
    columnGroupingModel,
    maxHeight,
    sx,
    processRowUpdate,
    handleRowModesModelChange,
    rowModesModel,
    handleRowEditStop,
    checkboxSelection,
    id_status,
    hide_status,
    rowSelectionModel,
    setRowSelectionModel = () => {},
    apiRef,
    showToolbar,
    getRowHeight,
    hideFooter,
    loading,
    additional,
}) {
    // 그리드가 언마운트되면 React가 apiRef.current를 null로 비운다(useImperativeHandle
    // 규약). 화면은 살아 있어 같은 ref로 그리드를 다시 그릴 때(핫 리로드·화면 전환
    // 복귀) MUI prop 검증이 그 null을 보고 콘솔 경고를 찍으므로, MUI 초기값과 같은
    // 빈 객체로 계약을 복원한다.
    if (apiRef && apiRef.current === null) {
        apiRef.current = {};
    }

    const { initialState: additionalInitialState, ...restAdditional } = additional ?? {};
    const mergedInitialState = React.useMemo(() => {
        const base = {
            columns: {
                columnVisibilityModel: {
                    id: id_status,
                    ...hide_status,
                },
            },
        };
        if (!additionalInitialState) return base;
        return {
            ...base,
            ...additionalInitialState,
            columns: {
                ...base.columns,
                ...additionalInitialState.columns,
                columnVisibilityModel: {
                    ...base.columns.columnVisibilityModel,
                    ...additionalInitialState.columns?.columnVisibilityModel,
                },
            },
            ...(additionalInitialState.filter
                ? {
                      filter: {
                          ...additionalInitialState.filter,
                          filterModel: {
                              items: [],
                              ...additionalInitialState.filter.filterModel,
                          },
                      },
                  }
                : {}),
        };
    }, [additionalInitialState, hide_status, id_status]);

    return (
        <DataGridPremium
            rows={rows}
            columns={cols}
            apiRef={apiRef}
            columnGroupingModel={columnGroupingModel}
            rowModesModel={rowModesModel}
            rowSelectionModel={rowSelectionModel}
            processRowUpdate={processRowUpdate}
            onRowModesModelChange={handleRowModesModelChange}
            onRowEditStop={handleRowEditStop}
            maxHeight={maxHeight}
            columnHeaderHeight={40}
            getRowId={(row) => row.id}
            rowHeight={40}
            getRowHeight={getRowHeight}
            disableRowSelectionOnClick
            disableAggregation
            checkboxSelection={checkboxSelection}
            keepNonExistentRowsSelected
            disableVirtualization // active when testing
            onRowSelectionModelChange={(newRowSelectionModel) => {
                setRowSelectionModel(newRowSelectionModel);
            }}
            hideFooter={hideFooter}
            loading={loading}
            experimentalFeatures={{ newEditingApi: true }}
            pagination={true}
            pageSizeOptions={[25, 50, 100]}
            slots={{
                toolbar:
                    showToolbar === 'CustomToolbar'
                        ? CustomToolbar
                        : showToolbar === 'GridToolbar'
                          ? GridToolbar
                          : false,
            }}
            slotProps={{
                toolbar: {
                    showQuickFilter: true,
                    printOptions: { disableToolbarButton: true },
                },
            }}
            initialState={mergedInitialState}
            {...restAdditional}
            sx={{
                fontSize: 13,
                border: 'none',
                justifyContent: 'space-between',
                overflow: 'visible',
                height: '81vh',
                // 잘못된 입력(음수 등)으로 오류 상태가 된 편집 셀을 빨간 테두리로 드러낸다.
                // 이 표시가 없으면 저장만 조용히 막혀 담당자가 이유를 알 수 없다.
                '& .MuiDataGrid-cell--editing .Mui-error': {
                    outline: '1.5px solid #d32f2f',
                    outlineOffset: '-1.5px',
                    backgroundColor: 'rgba(211, 47, 47, 0.08)',
                },
                '& .MuiDataGrid-main': {
                    borderRadius: 2,
                    boxShadow: 2,
                },
                '& .MuiDataGrid-columnHeader': {
                    backgroundColor: '#dfdfdf',
                    height: '50px',
                },
                '& .MuiDataGrid-scrollbarFiller': {
                    backgroundColor: '#dfdfdf',
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                    fontWeight: 'bold',
                    fontSize: '13px',
                },
                '& .MuiDataGrid-toolbarContainer': {
                    p: 0,
                    mb: '8.5px',
                },
                '& .MuiDataGrid-footerContainer': {
                    border: 'none',
                },
                '& .MuiButtonBase-root': {
                    fontSize: '13px',
                    fontWeight: 'bold',
                },
                ...sx,
            }}
        />
    );
}

export const handleTabKeyDown = (apiRef, editMode) => {
    if (!apiRef.current) return;

    return (params, event) => {
        // Custom tab key event handling.
        if (event.key === 'Tab') {
            const currentRowId = params.id;
            const currentField = params.field;
            const direction = event.shiftKey ? -1 : 1;

            // Get the currently visible columns in the data grid.
            const visibleColumns = apiRef.current.getVisibleColumns();

            // Current column index of the focused cell (excludes hidden columns).
            const currentColIndex = visibleColumns.findIndex((col) => col.field === currentField);

            const nextColIndex = currentColIndex + direction;

            const currentRowIndex = apiRef.current.getRowIndexRelativeToVisibleRows(currentRowId);

            // Move focus to the next cell if the column index is within valid bounds; otherwise, retain default behavior.
            if (nextColIndex >= 0 && nextColIndex < visibleColumns.length) {
                event.preventDefault();
                const nextField = visibleColumns[nextColIndex].field;

                // Scroll to the cell to keep it in view when focused.
                apiRef.current.scrollToIndexes({ currentRowIndex, colIndex: nextColIndex });
                apiRef.current.setCellFocus(currentRowId, nextField);
                // After tabbing away, Ctrl+C must copy the cell the user is on now —
                // otherwise the range highlighted before the Tab stays selected and
                // that stale range gets copied instead. Optional call: grids without
                // range selection (e.g. the shipment main table) don't have this API,
                // and Tab still has to move focus there.
                apiRef.current.setCellSelectionModel?.({ [currentRowId]: { [nextField]: true } });
            } else {
                const allDetailExpandedId = apiRef.current.getExpandedDetailPanels();
                const nextRowIndex = currentRowIndex + direction;
                // Get the current row's total row count.
                const totalRowNum = apiRef.current.getRowsCount();
                const allRowIds = apiRef.current.getAllRowIds();
                const nextRowId = allRowIds[nextRowIndex];
                const isDetailOpen = allDetailExpandedId.includes(currentRowId);

                let colIndex, nextField;

                // Move focus to the next cell if the row index is within valid bounds; otherwise, ratain default behavioir.
                if (nextRowIndex >= 0 && nextRowIndex < totalRowNum && !isDetailOpen) {
                    event.preventDefault();

                    if (direction === 1) {
                        colIndex = 0;
                        nextField = visibleColumns[colIndex].field;
                    } else if (direction === -1) {
                        colIndex = visibleColumns.length - 1;
                        nextField = visibleColumns[colIndex].field;
                    }

                    apiRef.current.scrollToIndexes({ nextRowIndex, colIndex });
                    apiRef.current.setCellFocus(nextRowId, nextField);
                    apiRef.current.setCellSelectionModel?.({ [nextRowId]: { [nextField]: true } });
                }
            }
        }
        // Custom enter key event handling.
        if (event.key === 'Enter') {
            // If the popper exists in the component, assume autocomplete is open.
            const autocompleteDom = document.querySelector('.MuiAutocomplete-popper');
            const autocompleteOpen = autocompleteDom !== null ? true : false;

            const currentMode = params.cellMode;

            if (autocompleteOpen) {
                event.preventDefault();
                event.stopPropagation();
            } else if (currentMode === 'edit') {
                event.preventDefault();
                event.stopPropagation();

                if (editMode === 'row') {
                    apiRef.current.stopRowEditMode({ id: params.id, field: params.field });
                } else apiRef.current.stopCellEditMode({ id: params.id, field: params.field });
            }
        }
    };
};

const ODD_OPACITY = 0.2;

export const StripedDataGrid = styled(DataGridPro)(({ theme }) => ({
    [`& .${gridClasses.row}.even`]: {
        backgroundColor: theme.palette.grey[200],
        '&:hover': {
            backgroundColor: alpha(theme.palette.secondary.main, ODD_OPACITY),
            '@media (hover: none)': {
                backgroundColor: 'transparent',
            },
        },
        '&.Mui-selected': {
            backgroundColor: alpha(theme.palette.primary.main, ODD_OPACITY + theme.palette.action.selectedOpacity),
            '&:hover': {
                backgroundColor: alpha(
                    theme.palette.secondary.main,
                    ODD_OPACITY + theme.palette.action.selectedOpacity + theme.palette.action.hoverOpacity,
                ),
                // Reset on touch devices, it doesn't add specificity
                '@media (hover: none)': {
                    backgroundColor: alpha(
                        theme.palette.secondary.main,
                        ODD_OPACITY + theme.palette.action.selectedOpacity,
                    ),
                },
            },
        },
        ...theme.applyStyles('dark', {
            backgroundColor: theme.palette.grey[800],
        }),
    },
}));
